import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import AvailabilityCalendar from "./AvailabilityCalendar";
import { useToast } from "../hooks/use-toast";
import { useCart } from "../context/CartContext";
import { fileUrl } from "../api";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { nl } from "date-fns/locale";
import { CalendarCheck, ShoppingCart, Check } from "lucide-react";

export const ONDERGROND_OPTIONS = [
  "Gras",
  "Tegels / bestrating",
  "Beton",
  "Kunstgras",
  "Zand",
  "Binnen (zaal)",
  "Anders / weet ik nog niet",
];

const eur = (v) => `€${Number(v).toFixed(2).replace(".", ",")}`;

const HuurModal = ({ product, open, onOpenChange }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { addItem, has } = useCart();
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [ondergrond, setOndergrond] = useState("");
  const [mode, setMode] = useState("single"); // 'single' | 'range'
  const [vullingen, setVullingen] = useState(0); // aantal extra 100-packs
  const [selectedFlavors, setSelectedFlavors] = useState([]);

  const reset = () => { setStart(null); setEnd(null); setOndergrond(""); setMode("single"); setVullingen(0); setSelectedFlavors([]); };
  const cover = (product.images && product.images[0]) || product.image;
  const hasVullingen = !!product.has_vullingen;
  const flavors = product.flavors || [];
  const toggleFlavor = (f) => setSelectedFlavors((prev) => prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]);

  const switchMode = (m) => { setMode(m); setStart(null); setEnd(null); };

  const days = start && end ? differenceInCalendarDays(parseISO(end), parseISO(start)) + 1 : (start ? 1 : 0);
  const total = days * Number(product.price) + vullingen * 10;

  const onRangeSelect = (s, e) => { setStart(s); setEnd(e); };
  const onSingleSelect = (d) => { setStart(d); setEnd(d); };

  const doAdd = (goToCart) => {
    if (!start) { toast({ title: "Kies een datum", description: "Selecteer eerst een beschikbare dag." }); return; }
    if (!hasVullingen && !ondergrond) { toast({ title: "Kies een ondergrond", description: "Geef aan op welke ondergrond het springkussen komt te staan." }); return; }
    const endDate = end || start;
    if (has(product.slug, start, endDate)) { toast({ title: "Staat al in je winkelwagen", description: `${product.name} op deze periode.` }); return; }
    addItem({ slug: product.slug, name: product.name, price: Number(product.price), image: cover, startDate: start, endDate, days, ondergrond, extraVullingen: hasVullingen ? vullingen : 0, flavor: flavors.length ? selectedFlavors.join(", ") : "" });
    onOpenChange(false); reset();
    if (goToCart) { navigate("/winkelwagen"); }
    else {
      const label = days > 1
        ? `${format(parseISO(start), "d MMM", { locale: nl })} t/m ${format(parseISO(endDate), "d MMM yyyy", { locale: nl })} · ${days} dagen`
        : format(parseISO(start), "d MMMM yyyy", { locale: nl });
      toast({ title: "Toegevoegd aan winkelwagen!", description: `${product.name} · ${label}` });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl text-brand-navy">Huur {product.name}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="flex items-center justify-between rounded-xl bg-brand-bluelight px-4 py-3">
            <span className="text-sm font-semibold text-brand-navy">Dagprijs</span>
            <span className="font-display font-bold text-brand-blue text-lg">€{product.price}</span>
          </div>

          <div>
            <Label className="font-semibold text-brand-navy">1. Kies je datum of periode</Label>
            <div className="mt-2 grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-full" data-testid="huur-mode-switch">
              <button
                type="button"
                onClick={() => switchMode("single")}
                data-testid="huur-mode-single"
                className={`rounded-full py-2 text-sm font-bold transition-all ${mode === "single" ? "bg-brand-blue text-white shadow-sm" : "text-slate-500 hover:text-brand-navy"}`}
              >
                1 dag
              </button>
              <button
                type="button"
                onClick={() => switchMode("range")}
                data-testid="huur-mode-range"
                className={`rounded-full py-2 text-sm font-bold transition-all ${mode === "range" ? "bg-brand-blue text-white shadow-sm" : "text-slate-500 hover:text-brand-navy"}`}
              >
                Meerdere dagen
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {mode === "single" ? "Klik de dag waarop je het springkussen wilt." : "Klik de eerste dag én de laatste dag van je periode."}
            </p>
            <div className="mt-3 rounded-2xl border border-slate-100 p-4">
              {mode === "single" ? (
                <AvailabilityCalendar
                  bookedDates={product.bookedDates || []}
                  selectable
                  selectedDate={start}
                  onSelect={onSingleSelect}
                />
              ) : (
                <AvailabilityCalendar
                  bookedDates={product.bookedDates || []}
                  selectable
                  mode="range"
                  rangeStart={start}
                  rangeEnd={end}
                  onRangeSelect={onRangeSelect}
                />
              )}
            </div>
            {start && (
              <p data-testid="huur-selected-range" className="flex items-center gap-2 text-sm text-brand-green font-semibold mt-2">
                <CalendarCheck className="w-4 h-4" />
                {days > 1
                  ? `${format(parseISO(start), "EEEE d MMM", { locale: nl })} t/m ${format(parseISO(end), "EEEE d MMM yyyy", { locale: nl })} · ${days} dagen`
                  : format(parseISO(start), "EEEE d MMMM yyyy", { locale: nl })}
              </p>
            )}
          </div>

          {hasVullingen ? (
            <>
              {flavors.length > 0 && (
                <div>
                  <Label className="font-semibold text-brand-navy">2. Kies je smaak / kleur <span className="text-slate-400 font-normal text-xs">(meerdere mogelijk)</span></Label>
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="huur-flavor-group">
                    {flavors.map((o) => {
                      const active = selectedFlavors.includes(o);
                      return (
                        <button
                          key={o}
                          type="button"
                          onClick={() => toggleFlavor(o)}
                          data-testid={`flavor-${o}`}
                          className={`inline-flex items-center gap-1.5 rounded-full border-2 px-3.5 py-2 text-sm font-semibold transition-all ${active ? "bg-brand-blue border-brand-blue text-white" : "bg-white border-slate-200 text-brand-navy hover:border-brand-blue"}`}
                        >
                          {active && <Check className="w-3.5 h-3.5" />} {o}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <Label className="font-semibold text-brand-navy">{flavors.length > 0 ? "3" : "2"}. Extra vullingen bijbestellen?</Label>
                <p className="text-xs text-slate-400 mt-1">100 vullingen (bakjes/stokjes) zijn inbegrepen. Extra per 100 stuks = €10.</p>
                <div className="mt-3 flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-brand-navy">{100 + vullingen * 100} vullingen totaal</p>
                    <p className="text-xs text-slate-400">100 inbegrepen{vullingen > 0 ? ` + ${vullingen * 100} extra` : ""}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button type="button" data-testid="vullingen-minus" onClick={() => setVullingen((v) => Math.max(0, v - 1))} className="w-9 h-9 rounded-full border-2 border-brand-blue text-brand-blue font-bold text-lg leading-none flex items-center justify-center hover:bg-brand-bluelight disabled:opacity-30" disabled={vullingen === 0}>−</button>
                    <span data-testid="vullingen-count" className="w-8 text-center font-display font-bold text-brand-navy">{vullingen}</span>
                    <button type="button" data-testid="vullingen-plus" onClick={() => setVullingen((v) => Math.min(50, v + 1))} className="w-9 h-9 rounded-full border-2 border-brand-blue text-brand-blue font-bold text-lg leading-none flex items-center justify-center hover:bg-brand-bluelight">+</button>
                  </div>
                </div>
                {vullingen > 0 && <p className="text-xs text-brand-green font-semibold mt-2">+ {vullingen} × 100 vullingen = {eur(vullingen * 10)}</p>}
              </div>
            </>
          ) : (
            <div>
              <Label className="font-semibold text-brand-navy">2. Op welke ondergrond komt het te staan? <span className="text-brand-pink">*</span></Label>
              <Select value={ondergrond} onValueChange={setOndergrond}>
                <SelectTrigger className="mt-2" data-testid="huur-ondergrond-select"><SelectValue placeholder="Kies de ondergrond" /></SelectTrigger>
                <SelectContent>
                  {ONDERGROND_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {start && (
            <div className="rounded-xl bg-brand-greenlight px-4 py-3 space-y-1" data-testid="huur-total">
              <div className="flex items-center justify-between text-sm text-brand-navy">
                <span>{days} {days === 1 ? "dag" : "dagen"} × €{product.price}</span>
                <span className="font-semibold">{eur(days * Number(product.price))}</span>
              </div>
              {hasVullingen && vullingen > 0 && (
                <div className="flex items-center justify-between text-sm text-brand-navy">
                  <span>{vullingen} × 100 extra vullingen</span>
                  <span className="font-semibold">{eur(vullingen * 10)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-1 border-t border-brand-green/20">
                <span className="text-sm font-semibold text-brand-navy">Totaal</span>
                <span className="font-display font-bold text-brand-green text-lg">{eur(total)}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <button onClick={() => doAdd(false)} data-testid="add-to-cart-button" className="rounded-full border-2 border-brand-blue text-brand-blue hover:bg-brand-bluelight font-bold py-3.5 transition-colors flex items-center justify-center gap-2">
              <ShoppingCart className="w-4 h-4" /> In winkelwagen
            </button>
            <button onClick={() => doAdd(true)} data-testid="add-and-checkout-button" className="rounded-full bg-brand-pink hover:bg-brand-pinkdark text-white font-bold py-3.5 transition-colors shadow-md flex items-center justify-center gap-2">
              <Check className="w-4 h-4" /> Direct afrekenen
            </button>
          </div>
          <p className="text-xs text-slate-400 text-center">Bezorgkosten & korting bereken je in de winkelwagen · gloednieuwe springkussens.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HuurModal;
